import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateOperationGroupsOptions,
  BulkCreateOperationGroupsResponse,
  BulkDeleteOperationGroupsResponse,
  BulkUpdateOperationGroupTagsApplicationErrors,
  BulkUpdateOperationGroupTagsByFilterApplicationErrors,
  BulkUpdateOperationGroupTagsByFilterOptions,
  BulkUpdateOperationGroupTagsByFilterResponse,
  BulkUpdateOperationGroupTagsOptions,
  BulkUpdateOperationGroupTagsResponse,
  BulkUpdateOperationGroupsOptions,
  BulkUpdateOperationGroupsResponse,
  MaskedOperationGroup,
  OperationGroup,
  OperationGroupCreatedEnvelope,
  OperationGroupDeletedEnvelope,
  OperationGroupQuery,
  OperationGroupUpdatedEnvelope,
  OperationGroupsQueryBuilder,
  QueryOperationGroupsResponse,
  UpdateOperationGroup,
  bulkCreateOperationGroups as universalBulkCreateOperationGroups,
  bulkDeleteOperationGroups as universalBulkDeleteOperationGroups,
  bulkUpdateOperationGroupTags as universalBulkUpdateOperationGroupTags,
  bulkUpdateOperationGroupTagsByFilter as universalBulkUpdateOperationGroupTagsByFilter,
  bulkUpdateOperationGroups as universalBulkUpdateOperationGroups,
  createOperationGroup as universalCreateOperationGroup,
  deleteOperationGroup as universalDeleteOperationGroup,
  getOperationGroup as universalGetOperationGroup,
  queryOperationGroups as universalQueryOperationGroups,
  typedQueryOperationGroups as universalTypedQueryOperationGroups,
  updateOperationGroup as universalUpdateOperationGroup,
} from './restaurants-v1-operation-group-operation-groups.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createOperationGroup(
  httpClient: HttpClient
): CreateOperationGroupSignature {
  return (operationGroup: NonNullablePaths<OperationGroup, `name`, 2>) =>
    universalCreateOperationGroup(
      operationGroup,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateOperationGroupSignature {
  /**
   * Creates a OperationGroup.
   * @param - OperationGroup to be created.
   * @returns The created OperationGroup.
   */
  (operationGroup: NonNullablePaths<OperationGroup, `name`, 2>): Promise<
    NonNullablePaths<OperationGroup, `tags.privateTags.tagIds`, 4>
  >;
}

export function getOperationGroup(
  httpClient: HttpClient
): GetOperationGroupSignature {
  return (operationGroupId: string) =>
    universalGetOperationGroup(
      operationGroupId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOperationGroupSignature {
  /**
   * Retrieves a OperationGroup.
   * @param - ID of the OperationGroup to retrieve.
   * @returns The requested OperationGroup.
   */
  (operationGroupId: string): Promise<
    NonNullablePaths<OperationGroup, `tags.privateTags.tagIds`, 4>
  >;
}

export function updateOperationGroup(
  httpClient: HttpClient
): UpdateOperationGroupSignature {
  return (
    _id: string,
    operationGroup: NonNullablePaths<UpdateOperationGroup, `revision`, 2>
  ) =>
    universalUpdateOperationGroup(
      _id,
      operationGroup,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateOperationGroupSignature {
  /**
   * Updates a OperationGroup.
   *
   *
   * Each time the OperationGroup is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the OperationGroup.
   * This ensures you're working with the latest OperationGroup
   * and prevents unintended overwrites.
   * @param - OperationsGroup ID.
   * @returns Updated OperationGroup.
   */
  (
    _id: string,
    operationGroup: NonNullablePaths<UpdateOperationGroup, `revision`, 2>
  ): Promise<NonNullablePaths<OperationGroup, `tags.privateTags.tagIds`, 4>>;
}

export function deleteOperationGroup(
  httpClient: HttpClient
): DeleteOperationGroupSignature {
  return (operationGroupId: string) =>
    universalDeleteOperationGroup(
      operationGroupId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteOperationGroupSignature {
  /**
   * Deletes a OperationGroup.
   *
   *
   * Deleting a OperationGroup permanently removes them from the OperationGroup List.
   * @param - Id of the OperationGroup to delete.
   */
  (operationGroupId: string): Promise<void>;
}

export function queryOperationGroups(
  httpClient: HttpClient
): QueryOperationGroupsSignature {
  return () =>
    universalQueryOperationGroups(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryOperationGroupsSignature {
  /**
   * Retrieves a list of OperationGroups.
   */
  (): OperationGroupsQueryBuilder;
}

export function typedQueryOperationGroups(
  httpClient: HttpClient
): TypedQueryOperationGroupsSignature {
  return (query: OperationGroupQuery) =>
    universalTypedQueryOperationGroups(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryOperationGroupsSignature {
  /** */
  (query: OperationGroupQuery): Promise<
    NonNullablePaths<QueryOperationGroupsResponse, `operationGroups`, 2>
  >;
}

export function bulkCreateOperationGroups(
  httpClient: HttpClient
): BulkCreateOperationGroupsSignature {
  return (
    operationGroups: NonNullablePaths<OperationGroup, `name`, 2>[],
    options?: BulkCreateOperationGroupsOptions
  ) =>
    universalBulkCreateOperationGroups(
      operationGroups,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateOperationGroupsSignature {
  /**
   * Create multiple OperationGroups in a single request. Works synchronously.
   * @param - List of OperationGroups to be created
   */
  (
    operationGroups: NonNullablePaths<OperationGroup, `name`, 2>[],
    options?: BulkCreateOperationGroupsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateOperationGroupsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateOperationGroups(
  httpClient: HttpClient
): BulkUpdateOperationGroupsSignature {
  return (
    operationGroups: NonNullablePaths<
      MaskedOperationGroup,
      `operationGroup.revision`,
      3
    >[],
    options?: BulkUpdateOperationGroupsOptions
  ) =>
    universalBulkUpdateOperationGroups(
      operationGroups,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOperationGroupsSignature {
  /**
   * Update multiple OperationGroups in a single request. Works synchronously.
   * @param - List of OperationGroups to be updated.
   */
  (
    operationGroups: NonNullablePaths<
      MaskedOperationGroup,
      `operationGroup.revision`,
      3
    >[],
    options?: BulkUpdateOperationGroupsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateOperationGroupsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteOperationGroups(
  httpClient: HttpClient
): BulkDeleteOperationGroupsSignature {
  return (operationGroupIds: string[]) =>
    universalBulkDeleteOperationGroups(
      operationGroupIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteOperationGroupsSignature {
  /**
   * Delete multiple OperationGroups in a single request. Works synchronously.
   * @param - OperationGroup ids to be deleted
   */
  (operationGroupIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteOperationGroupsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateOperationGroupTags(
  httpClient: HttpClient
): BulkUpdateOperationGroupTagsSignature {
  return (
    operationGroupIds: string[],
    options?: BulkUpdateOperationGroupTagsOptions
  ) =>
    universalBulkUpdateOperationGroupTags(
      operationGroupIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOperationGroupTagsSignature {
  /**
   * Synchronously update tags on multiple operation groups.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - IDs of the operation groups to update tags for.
   */
  (
    operationGroupIds: string[],
    options?: BulkUpdateOperationGroupTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateOperationGroupTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateOperationGroupTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateOperationGroupTagsByFilter(
  httpClient: HttpClient
): BulkUpdateOperationGroupTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateOperationGroupTagsByFilterOptions
  ) =>
    universalBulkUpdateOperationGroupTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOperationGroupTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple operation groups according to the specified filter.
   * If a filter isn't specified, this method updates all operation groups.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Filter that determines which operation groups to update tags for.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateOperationGroupTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateOperationGroupTagsByFilterResponse,
      `jobId`,
      2
    > & {
      __applicationErrorsType?: BulkUpdateOperationGroupTagsByFilterApplicationErrors;
    }
  >;
}

export const onOperationGroupCreated = /*#__PURE__*/ EventDefinition(
  'wix.restaurants.v1.operation_group_created',
  true,
  (event: OperationGroupCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage' }],
        },
      ])
    )
)<OperationGroupCreatedEnvelope>();
export const onOperationGroupDeleted = /*#__PURE__*/ EventDefinition(
  'wix.restaurants.v1.operation_group_deleted',
  true,
  (event: OperationGroupDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage' }],
        },
      ])
    )
)<OperationGroupDeletedEnvelope>();
export const onOperationGroupUpdated = /*#__PURE__*/ EventDefinition(
  'wix.restaurants.v1.operation_group_updated',
  true,
  (event: OperationGroupUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.coverImage' },
            { path: 'modifiedFields.coverImage' },
          ],
        },
      ])
    )
)<OperationGroupUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateOperationGroupsOptions,
  BulkCreateOperationGroupsRequest,
  BulkCreateOperationGroupsResponse,
  BulkDeleteOperationGroupsRequest,
  BulkDeleteOperationGroupsResponse,
  BulkDeleteOperationGroupsResponseBulkOperationGroupResult,
  BulkOperationGroupResult,
  BulkUpdateOperationGroupTagsByFilterOptions,
  BulkUpdateOperationGroupTagsByFilterRequest,
  BulkUpdateOperationGroupTagsByFilterResponse,
  BulkUpdateOperationGroupTagsOptions,
  BulkUpdateOperationGroupTagsRequest,
  BulkUpdateOperationGroupTagsResponse,
  BulkUpdateOperationGroupTagsResult,
  BulkUpdateOperationGroupsOptions,
  BulkUpdateOperationGroupsRequest,
  BulkUpdateOperationGroupsResponse,
  BulkUpdateOperationGroupsResponseBulkOperationGroupResult,
  CreateOperationGroupRequest,
  CreateOperationGroupResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteOperationGroupRequest,
  DeleteOperationGroupResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetOperationGroupRequest,
  GetOperationGroupResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MaskedOperationGroup,
  MessageEnvelope,
  OperationGroup,
  OperationGroupCreatedEnvelope,
  OperationGroupDeletedEnvelope,
  OperationGroupQuerySpec,
  OperationGroupUpdatedEnvelope,
  OperationGroupsQueryBuilder,
  OperationGroupsQueryResult,
  QueryOperationGroupsRequest,
  QueryOperationGroupsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  UpdateOperationGroup,
  UpdateOperationGroupRequest,
  UpdateOperationGroupResponse,
  WebhookIdentityType,
  utils,
} from './restaurants-v1-operation-group-operation-groups.universal.js';
